import { Link } from "react-router-dom";
import { ShieldCheck, Smartphone, Zap, ArrowRight, Ruler } from "lucide-react";
import { AdSlot } from "@/components/AdSlot";
import { Faq } from "@/components/Faq";
import { Seo } from "@/components/Seo";
import { JsonLd } from "@/components/JsonLd";
import { Card } from "@/components/ui/card";
import { PAGE_LIST, GROUPS } from "@/lib/landingpages";
import EvenPicturesCalculator from "@/components/calculators/EvenPicturesCalculator";
import { track } from "@/lib/track";

const GENERAL_FAQ = [
  { q: "Was kann ich mit Bohrpunkte.de berechnen?", a: "Du kannst Bilder gleichmäßig aufhängen, ein einzelnes Bild auf der gewünschten Höhe platzieren, ein Regal mittig montieren, Möbelgriffe bohren und eine Gardinenstange ausrichten. Der Rechner nennt dir die Bohrpunkte, Abstände und die Bohrhöhe." },
  { q: "Wie funktioniert die interaktive Vorschau?", a: "Du kannst die Maße direkt in der Skizze ziehen, etwa die Wandkante, die Objektgröße oder die Höhe. Die Bohrpunkte und Abstände rechnen sich dabei live mit. Genauso kannst du die Werte unten von Hand eintragen." },
  { q: "Ist das Tool kostenlos?", a: "Ja. Du kannst alle Rechner kostenlos und ohne Anmeldung nutzen." },
  { q: "Werden meine Eingaben gespeichert?", a: "Nein. Die Berechnung läuft vollständig in deinem Browser. Es werden keine persönlichen Daten gespeichert." },
  { q: "In welcher Einheit rechne ich?", a: "In Zentimetern. Die Ergebnisse sind auf Millimeter genau gerundet." },
  { q: "Ersetzt der Rechner die Wasserwaage?", a: "Nein. Der Rechner liefert die Maße. Die genaue waagerechte Ausrichtung prüfst du vor dem Bohren mit einer Wasserwaage. Achte außerdem auf Leitungen und die Wandbeschaffenheit." },
];

export default function HomePage() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const groupOrder = [GROUPS.bilder, GROUPS.regal, GROUPS.moebel, GROUPS.grundlagen];
  const grouped = groupOrder
    .map((g) => ({ group: g, items: PAGE_LIST.filter((p) => p.group === g) }))
    .filter((x) => x.items.length > 0);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Bohrpunkte.de",
      url: origin || undefined,
      inLanguage: "de-DE",
      description: "Bohrpunkte, Abstände und Bohrhöhen für Bilder, Regale und Wandobjekte berechnen, mit visueller Vorschau.",
    },
    {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Bohrpunkte.de",
      applicationCategory: "UtilitiesApplication",
      operatingSystem: "Web",
      url: origin || undefined,
      inLanguage: "de-DE",
      offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
      description: "Berechne Bohrpunkte, Abstände und Bohrhöhen für Bilder, Regale und Wandobjekte. Mit Live-Vorschau, kostenlos und ohne Anmeldung.",
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: GENERAL_FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div>
      <Seo
        title="Bohrpunkte berechnen: Bilder, Regale und Wandobjekte ausrichten"
        description="Berechne Bohrpunkte, Abstände und Bohrhöhen für Bilder, Regale und Wandobjekte. Mit Live-Vorschau, kostenlos und ohne Anmeldung."
      canonical={origin ? `${origin}/` : undefined}
      />
      <JsonLd data={jsonLd} />

      {/* Hero + Rechner above the fold */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-0"
          style={{ background: "linear-gradient(135deg, rgba(15,118,110,0.10), rgba(249,115,22,0.08), rgba(251,250,247,0))", height: "55%" }}
        />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12 pb-6 relative">
          <div className="max-w-2xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              Bohrpunkte und Abstände berechnen
            </h1>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground">
              Bilder, Regale und Wandobjekte sauber, mittig und gleichmäßig ausrichten. Trag die Maße ein, der Rechner zeigt dir Bohrpunkte und Bohrhöhe mit Live-Vorschau.
            </p>
            <div className="mt-5 flex flex-wrap gap-4 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-primary" /> Ohne Anmeldung</span>
              <span className="inline-flex items-center gap-1.5"><Smartphone className="h-4 w-4 text-primary" /> Mobil optimiert</span>
              <span className="inline-flex items-center gap-1.5"><Zap className="h-4 w-4 text-primary" /> Live berechnet</span>
            </div>
          </div>

          <div className="mt-6 lg:mt-8">
            <h2 className="sr-only">Bilder gleichmäßig aufhängen</h2>
            <EvenPicturesCalculator />
          </div>
        </div>
      </section>

      {/* Anzeige direkt unter dem Editor (Payoff-Moment), volle Breite, alle Geräte */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-4"><AdSlot variant="banner" /></div>

      {/* Use-Case-Auswahl */}
      <section id="tools" className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <h2 className="text-xl sm:text-2xl font-semibold">Welcher Rechner passt zu dir?</h2>
        <p className="text-muted-foreground mt-1">Wähle deinen Anwendungsfall. Jeder Rechner hat eine eigene Anleitung und Beispiele.</p>
        <div className="mt-6 space-y-8">
          {grouped.map(({ group, items }) => (
            <div key={group}>
              <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">{group}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map((p) => (
                  <Link key={p.slug} to={`/${p.slug}`} data-testid={`tool-link-${p.slug}`}>
                    <Card className="p-4 h-full hover:shadow-md transition-shadow border flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2.5">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-secondary text-primary shrink-0">
                          <Ruler className="h-4 w-4" />
                        </span>
                        <span className="text-sm font-medium">{p.navLabel}</span>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* So funktioniert es */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-12">
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { n: "1", t: "Maße eingeben", d: "Trag Wandbreite, Objektmaße und gewünschte Höhe ein." },
            { n: "2", t: "Vorschau prüfen", d: "Die Skizze zeigt Wand, Objekte, Bohrpunkte und Maße sofort." },
            { n: "3", t: "Bohrpunkte übertragen", d: "Markiere die Punkte, prüfe mit der Wasserwaage und bohre." },
          ].map((s) => (
            <Card key={s.n} className="p-5 border">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground font-heading">{s.n}</span>
              <p className="font-heading font-semibold mt-3">{s.t}</p>
              <p className="text-sm text-muted-foreground mt-1">{s.d}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Crosslink */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-6">
        <Card className="p-5 border flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="text-sm text-muted-foreground flex-1">Baust du ein Regal? Der Regalböden-Abstandsrechner liefert die Markiermaße.</p>
          <a href="https://regalabstand.de" className="text-sm font-medium hover:underline shrink-0" onClick={() => track("crosslink_click", { target: "regalabstand" })}>Zum Regalabstand-Rechner</a>
        </Card>
      </div>

      {/* Anzeige zwischen Inhalt und FAQ */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-6"><AdSlot variant="banner" /></div>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-12">
        <h2 className="text-xl sm:text-2xl font-semibold mb-4">Häufige Fragen</h2>
        <Faq items={GENERAL_FAQ} testId="home-faq-accordion" />
      </section>

      {/* Anzeige am Seitenende */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-12"><AdSlot variant="banner" /></div>
    </div>
  );
}
